// The call's live state as plain text, handed to the prep chat so Breeze can
// answer from what is being said and noted right now. Pure: the call screens
// pass in what their stores hold.

import type { AssistantHostState, PanelState } from "@breeze/core";

export interface LiveInput {
  readonly shared: PanelState | null;
  /** What the live assistant suggested saying next, if anything. */
  readonly say: string | null;
  /** Agenda points the live assistant marked covered. */
  readonly covered: readonly string[];
  /** Problems a guest described and what could be built for each. */
  readonly help?: readonly string[];
}

/** Long enough for a full call's notes, short enough to ride along every message. */
export const LIVE_LIMIT = 4000;

function list(title: string, items: readonly { text: string; asked?: boolean; done?: boolean; owner?: string }[]): string {
  if (items.length === 0) return "";
  const lines = items.map((i) => {
    const flag = i.asked ? " (asked)" : i.done ? " (done)" : "";
    const who = i.owner ? ` [${i.owner}]` : "";
    return `- ${i.text.trim()}${who}${flag}`;
  });
  return `${title}:\n${lines.join("\n")}`;
}

/** The call's stores as live text: the shared lists, plus (host only) the
 * agenda points marked covered, by title, and the line suggested next. */
export function liveFromCall(shared: PanelState | null, host: AssistantHostState | null): string {
  const titles = new Map((host?.agenda?.items ?? []).map((i) => [i.id, i.title] as const));
  return liveText({
    shared,
    say: host?.say?.line ?? null,
    covered: (host?.covered ?? []).map((id) => titles.get(id) ?? id),
    help: (host?.help ?? []).map((m) => `${m.who}: ${m.problem}. Build: ${m.build}`),
  });
}

export function liveText({ shared, say, covered, help = [] }: LiveInput): string {
  const parts: string[] = [];
  if (shared) {
    parts.push(list("Shared notes", shared.notes));
    parts.push(list("Questions", shared.questions));
    parts.push(list("Next steps", shared.steps));
  }
  if (covered.length > 0) parts.push(`Covered so far:\n${covered.map((c) => `- ${c}`).join("\n")}`);
  if (help.length > 0) parts.push(`Problems the guest described, with what could be built:\n${help.map((h) => `- ${h}`).join("\n")}`);
  if (say && say.trim()) parts.push(`Breeze suggested saying next: ${say.trim()}`);
  const text = parts.filter(Boolean).join("\n\n");
  return text.length > LIVE_LIMIT ? `${text.slice(0, LIVE_LIMIT - 1)}…` : text;
}
